import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import { ApiError, type Analise } from '../api/types';
import { iconePara } from '../assets/items';
import { existe, numero, t, umaCasa } from '../i18n/idioma';
import { nomeDaSkill, nomeDoInimigo, nomeDoItem, nomeDoMapa } from '../i18n/nomes';
import { dica, rotulo } from '../ui/dica';
import { MedidorDano, type DpsSnapshot } from '../world/dps';
import { FaceMob } from './RetratoMob';
import { Spark, type StrikeOuvir } from './DpsGump';

/*
  Análise de caçada (techspec-hunt-analyser; protótipo docs/prototipos/hunt-analyser-prototipo.html). Só VIP: a
  sessão mora no servidor (GET /api/analise a cada 2 s com a janela aberta). O gráfico de 2 min da aba Combate é o
  medidor de dano de sempre (world/dps.ts), ao vivo no cliente.
*/

const LER_MS = 2000;
const CHAVE_COMPACTO = 'uo.analise.compacto';
type Aba = 'saque' | 'gastos' | 'skills' | 'combate' | 'mobs';
const ABAS: readonly Aba[] = ['saque', 'gastos', 'skills', 'combate', 'mobs'];

interface Props {
  /** VIP ligado agora (o servidor confere de novo). */
  vip: boolean;
  listenStrike: (fn: StrikeOuvir | null) => void;
  listenStrikeCap: (fn: (() => void) | null) => void;
  /** Abre a loja na aba VIP. */
  onVerVip(): void;
}

const lerCompacto = () => {
  try { return localStorage.getItem(CHAVE_COMPACTO) === '1'; } catch { return false; }
};
const gravarCompacto = (v: boolean) => {
  try { localStorage.setItem(CHAVE_COMPACTO, v ? '1' : '0'); } catch { /* sem armazenamento: só nesta aba */ }
};

const sinal = (n: number) => `${n >= 0 ? '+' : '−'}${numero(Math.abs(Math.round(n)))}`;
const relogio = (s: number) => {
  const h = Math.floor(s / 3600), m = Math.floor(s / 60) % 60, ss = s % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
};
const nomeDoLugar = (id: string) => (existe(`portal.destino.${id}`) ? t(`portal.destino.${id}`) : nomeDoMapa(id));
const hora = (ms: number) => new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/** As contas da sessão: totais, ritmos por hora e o resumo para copiar. */
function contas(a: Analise) {
  const saque = a.prata + a.saque.reduce((s, i) => s + i.valor, 0);
  const gastos = a.gastos.reduce((s, g) => s + g.valor, 0);
  const saldo = saque - gastos;
  const abates = a.mobs.reduce((s, m) => s + m.abates, 0);
  const horas = a.tempoS / 3600;
  const ph = (v: number) => (a.tempoS >= 60 ? v / horas : null);
  const dps = a.lutaS > 0 ? a.combate.dano / a.lutaS : null;
  const somaSkills = a.skills.reduce((s, k) => s + (k.agora - k.de), 0);
  return { saque, gastos, saldo, abates, ph, dps, somaSkills };
}

function porHora(v: number | null, comSinal = false) {
  if (v == null) return '—';
  return t('analise.porHora', { v: comSinal ? sinal(v) : numero(Math.round(v)) });
}

function resumo(a: Analise): string {
  const c = contas(a);
  const melhor = a.saque.find(i => i.raro) ?? a.saque[0];
  const linhas = [
    t('analise.resumo.l1', { lugar: nomeDoLugar(a.mapa), tempo: relogio(a.tempoS) }),
    t('analise.resumo.l2', { saldo: sinal(c.saldo), ph: porHora(c.ph(c.saldo), true), saque: numero(c.saque), gastos: numero(c.gastos) }),
    t('analise.resumo.l3', { xp: numero(a.xp), ph: porHora(c.ph(a.xp)),
      skills: a.skills.filter(k => k.agora > k.de).map(k => `${nomeDaSkill(k.id)} +${umaCasa(k.agora - k.de)}`).join(', ') || '—' }),
    t('analise.resumo.l4', { abates: numero(c.abates), ph: porHora(c.ph(c.abates)),
      mobs: a.mobs.map(m => `${nomeDoInimigo(m.defId, m.nome)} ${numero(m.abates)}`).join(', ') || '—' }),
    t('analise.resumo.l5', { dps: c.dps == null ? '—' : numero(Math.round(c.dps)), mortes: numero(a.combate.mortes) })
  ];
  if (melhor) linhas.push(t('analise.resumo.l6', { item: nomeDoItem(melhor.defId), valor: numero(melhor.valor) }));
  return linhas.join('\n');
}

/** O saldo acumulado no tempo, em vermelho abaixo de zero. */
function CurvaSaldo({ pts }: { pts: readonly number[] }) {
  const W = 364, H = 46, pad = 3;
  const v = pts.length > 1 ? pts : [0, ...pts];
  const max = Math.max(0, ...v), min = Math.min(0, ...v);
  const x = (i: number) => (i / (v.length - 1)) * W;
  const y = (n: number) => pad + (H - 2 * pad) * (1 - (n - min) / (max - min || 1));
  const linha = v.map((n, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(n).toFixed(1)}`).join('');
  const zero = y(0);
  const area = `${linha}L${W},${zero.toFixed(1)}L0,${zero.toFixed(1)}Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <clipPath id="an-cima"><rect x="0" y="0" width={W} height={zero} /></clipPath>
        <clipPath id="an-baixo"><rect x="0" y={zero} width={W} height={H} /></clipPath>
      </defs>
      <path d={area} fill="#7fcf7a22" clipPath="url(#an-cima)" />
      <path d={area} fill="#e0857a33" clipPath="url(#an-baixo)" />
      <line x1="0" x2={W} y1={zero} y2={zero} stroke="#ffffff22" strokeDasharray="3 3" />
      <path d={linha} fill="none" stroke="#7fcf7a" strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function Icone({ src }: { src: string }) {
  return (
    <span className="an-ln__i">
      <img src={src} alt="" draggable={false}
        onLoad={e => { const i = e.currentTarget; if (i.naturalWidth < 15 && i.naturalHeight < 15) i.style.width = `${i.naturalWidth * 2}px`; }} />
    </span>
  );
}

/** A prévia de quem não é VIP: números fixos de exemplo, borrados, e o botão da loja. */
export function AnalisePrevia({ onVerVip }: { onVerVip(): void }) {
  return (
    <div className="an-previa">
      <div className="an-previa__fundo" aria-hidden="true">
        <div className="an-kpis">
          {['+13.216', '16.836', '−3.620', '30.805', '312', '49'].map((n, i) => (
            <div key={i} className="an-kpi"><span>{t(`analise.k.${['saldo', 'saque', 'gastos', 'xp', 'abates', 'dps'][i]}`)}</span><b>{n}</b></div>
          ))}
        </div>
        <div className="an-previa__linhas"><i /><i /><i /><i /><i /></div>
      </div>
      <div className="an-previa__cta">
        <span className="an-vip">VIP</span>
        <h4>{t('analise.titulo')}</h4>
        <p>{t('analise.pr.texto')}</p>
        <ul>
          <li>{t('analise.pr.i1')}</li>
          <li>{t('analise.pr.i2')}</li>
          <li>{t('analise.pr.i3')}</li>
          <li>{t('analise.pr.i4')}</li>
        </ul>
        <button type="button" className="an-btz" onClick={onVerVip}>{t('analise.pr.botao')}</button>
      </div>
    </div>
  );
}

export function AnaliseGump({ vip, listenStrike, listenStrikeCap, onVerVip }: Props) {
  const [a, setA] = useState<Analise | null>(null);
  const [semVip, setSemVip] = useState(!vip);
  const [erro, setErro] = useState(false);
  const [aba, setAba] = useState<Aba>('saque');
  const [compacto, setCompacto] = useState(lerCompacto);
  const [confirma, setConfirma] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [busy, setBusy] = useState(false);

  // O gráfico de 2 min: o medidor de dano de sempre, ao vivo.
  const medidor = useRef(new MedidorDano());
  const [snap, setSnap] = useState<DpsSnapshot>(() => medidor.current.snapshot());
  useEffect(() => {
    listenStrike((s, meu, emVoce, nome) => medidor.current.aplicar(s, meu, emVoce, nome));
    listenStrikeCap(() => medidor.current.marcarTeto());
    const id = window.setInterval(() => setSnap(medidor.current.snapshot()), 1000);
    return () => { listenStrike(null); listenStrikeCap(null); window.clearInterval(id); };
  }, [listenStrike, listenStrikeCap]);

  const tratar = useCallback((e: unknown) => {
    if (e instanceof ApiError && e.code === 'analise.vip') { setSemVip(true); return; }
    setErro(true);
  }, []);

  useEffect(() => { setSemVip(!vip); }, [vip]);

  useEffect(() => {
    if (semVip) return;
    let vivo = true;
    const ler = () => {
      if (document.hidden) return;
      api.getAnalise().then(r => { if (vivo) { setA(r); setErro(false); } }).catch(e => { if (vivo) tratar(e); });
    };
    ler();
    const id = window.setInterval(ler, LER_MS);
    return () => { vivo = false; window.clearInterval(id); };
  }, [semVip, tratar]);

  const c = useMemo(() => (a ? contas(a) : null), [a]);

  if (semVip) return <AnalisePrevia onVerVip={onVerVip} />;
  if (!a || !c) return <p className="an-vazio">{erro ? t('analise.erro') : t('comum.carregando')}</p>;

  const mudarCompacto = (v: boolean) => { setCompacto(v); gravarCompacto(v); };
  const pausar = () => {
    setBusy(true);
    api.pausarAnalise(!a.pausada).then(setA).catch(tratar).finally(() => setBusy(false));
  };
  const zerar = () => {
    setBusy(true);
    api.zerarAnalise().then(r => { setA(r); setConfirma(false); }).catch(tratar).finally(() => setBusy(false));
  };
  const copiar = () => {
    void navigator.clipboard?.writeText(resumo(a)).then(() => {
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 1400);
    }).catch(() => {});
  };

  const estado = a.pausada ? 'pausado' : a.parada ? 'parado' : 'caca';
  const lugar = a.mapa ? nomeDoLugar(a.mapa) : t('analise.semLugar');

  if (compacto) {
    return (
      <div className="an-mini">
        <div {...dica({ titulo: t('analise.d.relogio.t'), texto: t('analise.d.relogio.x') })}><span>{lugar}</span><b>{relogio(a.tempoS)}</b></div>
        <div {...dica({ titulo: t('analise.d.saldo.t'), texto: t('analise.d.saldo.x') })}><span>{t('analise.k.saldoH')}</span><b className={c.saldo >= 0 ? 'pos' : 'neg'}>{c.ph(c.saldo) == null ? '—' : sinal(c.ph(c.saldo)!)}</b></div>
        <div {...dica({ titulo: t('analise.d.xp.t'), texto: t('analise.d.xp.x') })}><span>{t('analise.k.xpH')}</span><b className="xp">{c.ph(a.xp) == null ? '—' : numero(Math.round(c.ph(a.xp)!))}</b></div>
        <div {...dica({ titulo: t('analise.d.abates.t'), texto: t('analise.d.abates.x') })}><span>{t('analise.k.abatesH')}</span><b>{c.ph(c.abates) == null ? '—' : numero(Math.round(c.ph(c.abates)!))}</b></div>
        <div {...dica({ titulo: t('analise.d.dps.t'), texto: t('analise.d.dps.x') })}><span>{t('analise.k.dps')}</span><b>{c.dps == null ? '—' : numero(Math.round(c.dps))}</b></div>
        <button type="button" className="an-mini__abre" {...rotulo(t('analise.expandir'))} onClick={() => mudarCompacto(false)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" /></svg>
        </button>
      </div>
    );
  }

  const totSaque = Math.max(1, c.saque), totGasto = Math.max(1, c.gastos), totAbates = Math.max(1, c.abates);
  const n: Record<Aba, number> = { saque: a.saque.length + (a.prata > 0 ? 1 : 0), gastos: a.gastos.length, skills: a.skills.length, combate: 0, mobs: a.mobs.length };

  const lista = () => {
    if (aba === 'saque') {
      const linhas = [
        ...(a.prata > 0 ? [{ defId: 'silver', qtd: a.prata, valor: a.prata, raro: false, semPreco: false }] : []),
        ...a.saque
      ].sort((x, y) => y.valor - x.valor);
      if (!linhas.length) return <p className="an-vazio">{t('analise.vazio')}</p>;
      return linhas.map(i => (
        <div key={i.defId} className={`an-ln${i.raro ? ' is-raro' : ''}`}
          {...dica({ titulo: nomeDoItem(i.defId), texto: i.semPreco ? t('analise.d.semPreco') : t('analise.d.itemSaque', { qtd: numero(i.qtd), valor: numero(i.valor) }), rodape: i.raro ? t('analise.raro') : undefined })}>
          <Icone src={iconePara(i.defId, i.qtd)} />
          <span className="an-ln__n"><b>{i.defId === 'silver' ? t('analise.moedas') : nomeDoItem(i.defId)}</b>
            <small>{i.raro ? `${t('analise.raro')} · ` : ''}× {numero(i.qtd)}{i.semPreco ? ` · ${t('analise.semPreco')}` : ''}</small></span>
          <span className="an-ln__v">{numero(i.valor)}<small>{Math.round((100 * i.valor) / totSaque)}%</small></span>
          <span className="an-ln__barra"><i style={{ width: `${(100 * i.valor) / totSaque}%` }} /></span>
        </div>
      ));
    }
    if (aba === 'gastos') {
      if (!a.gastos.length) return <p className="an-vazio">{t('analise.vazio')}</p>;
      return a.gastos.map(g => (
        <div key={g.defId} className="an-ln" {...dica({ titulo: nomeDoItem(g.defId), texto: t('analise.d.itemGasto', { qtd: numero(g.qtd), valor: numero(g.valor) }) })}>
          <Icone src={iconePara(g.defId, g.qtd)} />
          <span className="an-ln__n"><b>{nomeDoItem(g.defId)}</b><small>× {numero(g.qtd)} · {porHora(c.ph(g.qtd))}</small></span>
          <span className="an-ln__v neg">−{numero(g.valor)}<small>{Math.round((100 * g.valor) / totGasto)}%</small></span>
          <span className="an-ln__barra is-neg"><i style={{ width: `${(100 * g.valor) / totGasto}%` }} /></span>
        </div>
      ));
    }
    if (aba === 'skills') {
      return (
        <>
          <div className="an-sub">{t('analise.skillsTopo', { xp: numero(a.xp), ph: porHora(c.ph(a.xp)), soma: umaCasa(c.somaSkills) })}</div>
          {!a.skills.length && <p className="an-vazio">{t('analise.vazio')}</p>}
          {a.skills.map(k => {
            const d = k.agora - k.de;
            const passos = Math.round(d * 10);
            const cada = passos > 0 && a.tempoS > 0 ? Math.max(1, Math.round(a.tempoS / 60 / passos)) : null;
            const pct = k.custo > 0 ? Math.min(100, Math.round((100 * k.barra) / k.custo)) : 0;
            return (
              <div key={k.id} className="an-ln" {...dica({ titulo: nomeDaSkill(k.id), texto: t('analise.d.skill', { de: umaCasa(k.de), para: umaCasa(k.agora), pct }) })}>
                <span className="an-ln__i an-ln__d">+{umaCasa(d)}</span>
                <span className="an-ln__n"><b>{nomeDaSkill(k.id)}</b>
                  <small>{umaCasa(k.de)} → {umaCasa(k.agora)}{cada ? ` · ${t('analise.skillCada', { min: cada })}` : ''}</small>
                  <span className="an-xp"><i style={{ width: `${pct}%` }} /></span></span>
                <span className="an-ln__v xp">{umaCasa(k.agora)}</span>
              </div>
            );
          })}
        </>
      );
    }
    if (aba === 'combate') {
      const cb = a.combate;
      const cura = cb.curaBandagem + cb.curaPocao + cb.curaMagia;
      const pct = (x: number, y: number) => (y > 0 ? `${Math.round((100 * x) / y)}%` : '—');
      return (
        <>
          <div className="an-grade">
            <div><span>{t('analise.c.dano')}</span><b>{numero(cb.dano)}</b></div>
            <div><span>{t('analise.c.dps')}</span><b>{c.dps == null ? '—' : `${numero(Math.round(c.dps))}/s`}</b></div>
            <div><span>{t('analise.c.criticos')}</span><b>{pct(cb.criticos, cb.golpes)}</b></div>
            <div><span>{t('analise.c.maior')}</span><b>{numero(cb.maior)}</b></div>
            <div><span>{t('analise.c.sofrido')}</span><b className="neg">{numero(cb.sofrido)}</b></div>
            <div><span>{t('analise.c.esquivas')}</span><b>{pct(cb.esquivas, cb.recebidos)}</b></div>
            <div><span>{t('analise.c.curado')}</span><b className="pos">{numero(cura)}</b></div>
            <div><span>{t('analise.c.emLuta')}</span><b>{pct(a.lutaS, a.tempoS)}</b></div>
            <div><span>{t('analise.c.bandagem')}</span><b>{numero(cb.curaBandagem)}</b></div>
            <div><span>{t('analise.c.pocoes')}</span><b>{numero(cb.curaPocao)}</b></div>
            <div><span>{t('analise.c.magia')}</span><b>{numero(cb.curaMagia)}</b></div>
            <div><span>{t('analise.c.mortes')}</span><b>{numero(cb.mortes)}</b></div>
          </div>
          {snap.paredeS >= 5 && <div className="an-spark"><Spark snap={snap} /></div>}
        </>
      );
    }
    if (!a.mobs.length) return <p className="an-vazio">{t('analise.vazio')}</p>;
    return a.mobs.map(m => (
      <div key={m.defId} className="an-ln" {...dica({ titulo: nomeDoInimigo(m.defId, m.nome), texto: t('analise.d.mob', { abates: numero(m.abates), xp: numero(m.xp) }) })}>
        <span className="an-ln__i an-ln__mob"><FaceMob aparencia={m.aparencia} px={26} /></span>
        <span className="an-ln__n"><b>{nomeDoInimigo(m.defId, m.nome)}</b><small>{porHora(c.ph(m.abates))} · {numero(m.xp)} XP</small></span>
        <span className="an-ln__v">{numero(m.abates)}<small>{Math.round((100 * m.abates) / totAbates)}%</small></span>
        <span className="an-ln__barra"><i style={{ width: `${(100 * m.abates) / totAbates}%` }} /></span>
      </div>
    ));
  };

  return (
    <div className="an">
      <div className="an-sessao">
        <span className="an-lugar"><span className={`an-ponto is-${estado}`} />{lugar} · {t(`analise.estado.${estado}`)}</span>
        <span className="an-relogio" {...dica({ titulo: t('analise.d.relogio.t'), texto: t('analise.d.relogio.x') })}>{relogio(a.tempoS)}</span>
        <button type="button" className={`an-ic${a.pausada ? ' is-on' : ''}`} disabled={busy}
          {...dica({ titulo: t(a.pausada ? 'analise.continuar' : 'analise.pausar'), texto: t(a.pausada ? 'analise.d.continuar' : 'analise.d.pausar') })}
          onClick={pausar}>
          {a.pausada
            ? <svg viewBox="0 0 24 24" fill="currentColor"><path d="M7 5l12 7-12 7z" /></svg>
            : <svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>}
        </button>
        <button type="button" className="an-ic" {...dica({ titulo: t('analise.copiar'), texto: t('analise.d.copiar') })} onClick={copiar}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="8" y="8" width="12" height="12" rx="2" /><path d="M16 8V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3" /></svg>
        </button>
        <button type="button" className="an-ic" {...dica({ titulo: t('analise.compacto'), texto: t('analise.d.compacto') })} onClick={() => mudarCompacto(true)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 14h6v6M20 10h-6V4M14 10l6-6M4 20l6-6" /></svg>
        </button>
      </div>

      <div className="an-kpis">
        <div className={`an-kpi ${c.saldo >= 0 ? 'pos' : 'neg'}`} {...dica({ titulo: t('analise.d.saldo.t'), texto: t('analise.d.saldo.x') })}>
          <span>{t('analise.k.saldo')}</span><b>{sinal(c.saldo)}</b><small>{porHora(c.ph(c.saldo), true)}</small></div>
        <div className="an-kpi" {...dica({ titulo: t('analise.d.saque.t'), texto: t('analise.d.saque.x') })}>
          <span>{t('analise.k.saque')}</span><b>{numero(c.saque)}</b><small>{porHora(c.ph(c.saque))}</small></div>
        <div className="an-kpi neg" {...dica({ titulo: t('analise.d.gastos.t'), texto: t('analise.d.gastos.x') })}>
          <span>{t('analise.k.gastos')}</span><b>−{numero(c.gastos)}</b><small>{porHora(c.ph(c.gastos))}</small></div>
        <div className="an-kpi xp" {...dica({ titulo: t('analise.d.xp.t'), texto: t('analise.d.xp.x') })}>
          <span>{t('analise.k.xp')}</span><b>{numero(a.xp)}</b><small>{porHora(c.ph(a.xp))}</small></div>
        <div className="an-kpi" {...dica({ titulo: t('analise.d.abates.t'), texto: t('analise.d.abates.x') })}>
          <span>{t('analise.k.abates')}</span><b>{numero(c.abates)}</b><small>{porHora(c.ph(c.abates))}</small></div>
        <div className="an-kpi" {...dica({ titulo: t('analise.d.dps.t'), texto: t('analise.d.dps.x') })}>
          <span>{t('analise.k.dps')}</span><b>{c.dps == null ? '—' : numero(Math.round(c.dps))}</b><small>{t('analise.emLuta')}</small></div>
      </div>

      <div className="an-saldo" {...dica({ titulo: t('analise.d.curva.t'), texto: t('analise.d.curva.x') })}>
        <div className="an-saldo__cab"><span>{t('analise.saldoTitulo')}</span><span>{t('analise.saldoFatias', { min: Math.round(a.fatiaS / 60) })}</span></div>
        <CurvaSaldo pts={a.saldo} />
      </div>

      <div className="an-abas" role="tablist">
        {ABAS.map(x => (
          <button key={x} type="button" role="tab" aria-selected={aba === x} className={`an-aba${aba === x ? ' is-on' : ''}`} onClick={() => setAba(x)}>
            {t(`analise.aba.${x}`)}{n[x] > 0 && <small>{n[x]}</small>}
          </button>
        ))}
      </div>
      <div className="an-lista">{lista()}</div>

      <div className="an-pe">
        {confirma ? (
          <>
            <span className="an-pe__p">{t('analise.zerarConfirma')}</span>
            <button type="button" className="an-btz an-btz--nao" disabled={busy} onClick={() => setConfirma(false)}>{t('comum.cancelar')}</button>
            <button type="button" className="an-btz" disabled={busy} onClick={zerar}>{t('analise.zerar')}</button>
          </>
        ) : (
          <>
            <span className="an-pe__p">{a.ativa ? t('analise.desde', { hora: hora(a.desdeMs) }) : t('analise.semSessao')}</span>
            <button type="button" className="an-btz" disabled={busy} {...dica({ titulo: t('analise.zerar'), texto: t('analise.d.zerar') })} onClick={() => setConfirma(true)}>{t('analise.zerar')}</button>
          </>
        )}
      </div>
      {copiado && <div className="an-toast" role="status">{t('analise.copiado')}</div>}
    </div>
  );
}
